/*
 * Copyright (c) 2024. Tooling Team
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { MEDDOMAIN_EIS_BASE_URL } from "../../utils/constants";
import HeaderService from "../../services/headerService";
import { medDomainEndpoints } from "./medDomainApi";
import { addUniqueId } from "../../utils/funcs";
import { setMedDomainBuildSummaryEisData } from "../slices/medDomain.slice";
import { createAuthenticatedBaseQuery } from "./authenticatedBaseQuery";

const headerService = new HeaderService();

const medDomainEISBaseQuery = createAuthenticatedBaseQuery({
  baseUrl: MEDDOMAIN_EIS_BASE_URL,
  getHeaders: () => headerService.getAuthorizationHeader(),
});

const stripEndpointSideEffects = (endpointDefinitions) =>
  Object.fromEntries(
    Object.entries(endpointDefinitions).map(([endpointName, definition]) => {
      // Remove onQueryStarted from endpoint definitions without assigning it
      const safeDefinition = { ...definition };
      delete safeDefinition.onQueryStarted;
      return [endpointName, safeDefinition];
    })
  );

const medDomainEISEndpoints = (builder) => {
  const sharedEndpoints = stripEndpointSideEffects(medDomainEndpoints(builder));

  return {
    ...sharedEndpoints,
    getMedDomainBuildSummaryEisById: builder.query({
      query: (buildId) => `/v1/builds/summary/${buildId}`,
      transformResponse: (response) => response.results ?? [],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const summary = await queryFulfilled;
          const summaryWithUniqueId = addUniqueId(summary.data || []);
          dispatch(setMedDomainBuildSummaryEisData(summaryWithUniqueId));
        } catch (e) {
          console.log("Error fetching EIS build summary: ", e);
        }
      },
    }),
  };
};

export const medDomainEISApi = createApi({
  reducerPath: "medDomainEISApi",
  baseQuery: medDomainEISBaseQuery,
  endpoints: medDomainEISEndpoints,
});

export const {
  useGetAllMedDomainBuildsQuery: useGetAllMedDomainEISBuildsQuery,
  useLazyGetAllMedDomainBuildsQuery: useLazyGetAllMedDomainEISBuildsQuery,
  useGetMedDomainBuildSummaryEisByIdQuery:
    useGetMedDomainEISBuildSummaryByIdQuery,
  useLazyGetMedDomainBuildSummaryEisByIdQuery:
    useLazyGetMedDomainEISBuildSummaryByIdQuery,
} = medDomainEISApi;

export const selectGetAllMedDomainEISBuilds =
  medDomainEISApi.endpoints.getAllMedDomainBuilds.select();
